import { memo, useState, useCallback, useEffect } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Typography, TextField, Drawer, Box, Button } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
// Local files
import DrawerSkeleton from 'components/Common/BaseDrawerSkeleton/BaseDrawerSkeleton';
import BaseEventsList from 'components/Analyses/Common/BaseEventsList/BaseEventsList';
import ErrorWrapper from 'components/Common/ErrorWrapper/ErrorWrapper';
import useCustomSelector from 'hooks/useCustomSelector';
import useSeventhAnalyses from 'hooks/useSeventhAnalyses';
import useEvents from 'hooks/useEvents';
import { eventsSelectors } from "features/events";
import { seventhAnalysesSelectors } from 'features/seventhAnalyses';

const SeventhAnalysisDrawer = () => {
  const mediaDesktop = useMediaQuery(theme => theme.breakpoints.up('md'));
  const navigate = useNavigate();
  const { seventh_analysis_id } = useParams();
  const { getSeventhAnalysis, clearLocalActiveSeventhAnalysis } = useSeventhAnalyses();
  const { getSeventhAnalysesEvents, clearLocalAnalysesEvents } = useEvents();
  const { data, pagination } = useCustomSelector(eventsSelectors.getAnalysesEventsData);
  const eventsFetching = useCustomSelector(eventsSelectors.analysesEventsIsFetching);
  const seventhAnalysisFetching = useCustomSelector(seventhAnalysesSelectors.seventhAnalysisIsFetching);
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [result, setResult] = useState('');
  const [voice, setVoice] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [firstTimeEventsOpened, setFirstTimeEventsOpened] = useState(true);

  const fetchSeventhAnalysis = useCallback((id) => {
    setOpen(true);
    getSeventhAnalysis({ id })
    .then(({ payload, error }) => {
      if (!error) {
        const { data: { seventh_analysis } } = payload;
        handleSetData(seventh_analysis);
      }
    })
  }, [getSeventhAnalysis]);

  const handleSetData = (seventh_analysis) => {
    setInput(seventh_analysis?.input);
    setResult(seventh_analysis?.result);
    setVoice(seventh_analysis?.voice);
  };

  const handleExiting = () => {
    setInput('');
    setResult('');
    setVoice(null);
    setExpanded(false);
    setFirstTimeEventsOpened(true);
    navigate(`/flow/fl77e`);
    clearLocalActiveSeventhAnalysis();
    clearLocalAnalysesEvents();
  };

  const handleClose = () => setOpen(false);

  const fetchSeventhAnalysesEvents = useCallback((offset, seventh_analysis_id) => {
    getSeventhAnalysesEvents({ offset, seventh_analysis_id })
  }, [ getSeventhAnalysesEvents]);

  const handleChangeExpand = (_, isExpanded) => {
    setExpanded(isExpanded);
    if (firstTimeEventsOpened) {
      fetchSeventhAnalysesEvents(0, seventh_analysis_id);
      setFirstTimeEventsOpened(false);
    }
  }

  useEffect(() => {
    if (!!seventh_analysis_id) fetchSeventhAnalysis(seventh_analysis_id);
  }, [seventh_analysis_id, fetchSeventhAnalysis]);
  
  return (
    <Drawer
      anchor={!mediaDesktop ? 'bottom' : 'right'}
      open={open}
      SlideProps={{ onExited: handleExiting }}
      onClose={handleClose}
      ModalProps={{ sx: { zIndex: 1300 } }}
      PaperProps={{
        sx: {
          height: '100%',
          width: { xs: '100%', md: '600px'},
          backgroundColor: 'background.default'
        }
      }}
    >
      <Box>
        <Box sx={{ display: 'flex', p: '8px 8px 0' }}>
          <Button color='inherit' sx={{ fontWeight: 700 }} onClick={handleClose}>Close</Button>
          <div style={{ flexGrow: 1 }}/>
        </Box>
        <Box sx={{ fontSize: '2rem', fontWeight: 'bold', textAlign: 'center'}}>
            Request log
        </Box>
      </Box>
      <Box sx={{ flexGrow: 1, p: { xs: 2, md: '16px 100px 32px' } }}>
      { seventhAnalysisFetching ? <DrawerSkeleton /> :
        <>
          <Typography variant='body1' sx={{ fontWeight: 600, textAlign: 'center', mb: '10px' }}>Job Id: {seventh_analysis_id}</Typography>
          {!!voice?.url && <Box display='flex' justifyContent='center'>
            <audio controls controlsList="nodownload">
              <source src={voice.url} type="audio/wav"/>
              Your browser does not support the audio element.
            </audio>
          </Box>}
          <TextField
            fullWidth
            margin='normal'
            label='Input'
            multiline
            minRows={5}
            maxRows={8}
            disabled
            value={input ?? ''}
            sx={{
              "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: "#073842",
              }
            }}
          />
          <TextField
            fullWidth
            margin='normal'
            label='Result'
            multiline
            minRows={5}
            maxRows={8}
            disabled
            value={result ?? ''}
            sx={{
              "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: "#073842",
              }
            }}
          />
          <BaseEventsList
            data={data}
            pagination={pagination}
            analysisId={seventh_analysis_id}
            eventsFetching={eventsFetching}
            fetchEvents={fetchSeventhAnalysesEvents}
            expanded={expanded}
            handleChangeExpand={handleChangeExpand}
          />
        </>}
      </Box>
    </Drawer>
  );
};

const WrappedComponent = props => {
  return (
    <ErrorWrapper>
      <SeventhAnalysisDrawer {...props} />
    </ErrorWrapper>
  );
};

export default memo(WrappedComponent);